import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';

export interface OperatorData {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'operator';
  status: 'online' | 'offline' | 'away';
  activeChats: number;
  totalChats: number;
}

export interface OperatorStats {
  operators: OperatorData[];
  summary: {
    total: number;
    online: number;
    away: number;
    offline: number;
    totalActiveChats: number;
  };
  lastUpdated: string;
}

export interface UseOperatorStatsProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  onError?: (error: string) => void;
}

export const useOperatorStats = ({
  autoRefresh = true,
  refreshInterval = 60000, // 1分
  onError,
}: UseOperatorStatsProps = {}) => {
  const [stats, setStats] = useState<OperatorStats>({
    operators: [],
    summary: {
      total: 0,
      online: 0,
      away: 0,
      offline: 0,
      totalActiveChats: 0,
    },
    lastUpdated: new Date().toISOString(),
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // オペレーター統計データを取得
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      
      const response = await apiClient.getOperatorStats();
      
      if (response.success && response.operators) {
        // APIレスポンスから運営者データを変換
        const operators: OperatorData[] = response.operators.map((op: any) => ({
          id: String(op.id), // 文字列に変換してcreated_byと比較できるようにする
          name: op.displayName || op.username,
          email: op.email,
          role: op.role === 'support' ? 'operator' : 'admin',
          status: 'online', // 仮: 後でステータス管理を実装
          activeChats: 0, // 仮: 後でアクティブチャット数を取得
          totalChats: op.roomCount || 0,
        }));

        setStats({
          operators,
          summary: {
            total: operators.length,
            online: operators.filter(op => op.status === 'online').length,
            away: operators.filter(op => op.status === 'away').length,
            offline: operators.filter(op => op.status === 'offline').length,
            totalActiveChats: operators.reduce((sum, op) => sum + op.activeChats, 0),
          },
          lastUpdated: new Date().toISOString(),
        });
        setLastUpdated(new Date());
      } else {
        throw new Error('オペレーター統計の取得に失敗しました');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'オペレーター統計の取得に失敗しました';
      setError(errorMessage);
      onError?.(errorMessage);
      
      // エラー時はダミーデータを表示
      setStats({
        operators: [
          { 
            id: 'admin', 
            name: '管理者', 
            email: 'admin@bizimo.ne.jp', 
            role: 'admin', 
            status: 'online',
            activeChats: 2,
            totalChats: 15
          },
        ],
        summary: {
          total: 1,
          online: 1,
          away: 0,
          offline: 0,
          totalActiveChats: 2,
        },
        lastUpdated: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // 初回読み込み
  useEffect(() => {
    fetchStats();
  }, []); // 依存配列を空にして初回のみ実行

  // 自動更新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]); // fetchStatsを依存配列から削除して無限ループを防止

  // 手動更新
  const refresh = useCallback(() => {
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    lastUpdated,
    refresh,
  };
};

export default useOperatorStats;
